import moment from 'moment'

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export const FREQUENCY_OPTIONS = [
  {key: 'daily', text: 'day(s)', value: 'daily'},
  {key: 'weekly', text: 'week(s)', value: 'weekly'},
  {key: 'monthly', text: 'month(s)', value: 'monthly'},
  {key: 'yearly', text: 'year(s)', value: 'yearly'}
]

export const POSITION_OPTIONS = [
  {key: 'first', text: 'first', value: 'first'},
  {key: 'second', text: 'second', value: 'second'},
  {key: 'third', text: 'third', value: 'third'},
  {key: 'fourth', text: 'fourth', value: 'fourth'},
  {key: 'last', text: 'last', value: 'last'}
]

export function defaultRecurrence (startDate) {
  const start = startDate ? moment(startDate) : moment()
  return {
    frequency: 'weekly',
    interval: 1,
    days_of_week: [start.day()],
    monthly_type: 'day_of_month',
    day_of_month: start.date(),
    weekday: start.day(),
    weekday_position: 'first'
  }
}

export function recurrenceFromStartDate (recurrence, startDate) {
  if (!startDate) return recurrence
  const start = moment(startDate)
  const next = Object.assign({}, recurrence)
  if (!next.days_of_week || next.days_of_week.length === 0) {
    next.days_of_week = [start.day()]
  }
  if (!next.day_of_month) {
    next.day_of_month = start.date()
  }
  if (next.weekday == null) {
    next.weekday = start.day()
  }
  return next
}

function formatWeekdays (days) {
  if (!days || days.length === 0) return ''
  if (days.length === 7) return 'every day'
  if (days.length === 5 && [1, 2, 3, 4, 5].every(d => days.includes(d))) {
    return 'every weekday'
  }
  return days.map(d => WEEKDAY_NAMES[d]).join(', ')
}

export function describeRecurrence (recurrence, startDate) {
  if (!recurrence || !recurrence.frequency) return ''
  const interval = recurrence.interval || 1
  const anchor = startDate ? moment(startDate) : moment()

  switch (recurrence.frequency) {
    case 'daily':
      return interval === 1 ? 'Every day' : `Every ${interval} days`
    case 'weekly': {
      const days = formatWeekdays(recurrence.days_of_week && recurrence.days_of_week.length
        ? recurrence.days_of_week
        : [anchor.day()])
      if (interval === 1) return `Every week on ${days}`
      if (interval === 2) return `Every 2 weeks on ${days}`
      return `Every ${interval} weeks on ${days}`
    }
    case 'monthly':
      if (recurrence.monthly_type === 'day_of_week') {
        const weekday = WEEKDAY_NAMES[recurrence.weekday != null ? recurrence.weekday : anchor.day()]
        const position = recurrence.weekday_position || 'first'
        const positionLabel = position === 'last' ? 'last' : `the ${position}`
        if (interval === 1) return `Monthly on ${positionLabel} ${weekday}`
        return `Every ${interval} months on ${positionLabel} ${weekday}`
      }
      {
        const day = recurrence.day_of_month || anchor.date()
        if (interval === 1) return `Monthly on day ${day}`
        if (interval === 2) return `Every 2 months on day ${day}`
        if (interval === 3) return `Every 3 months on day ${day}`
        if (interval === 6) return `Every 6 months on day ${day}`
        return `Every ${interval} months on day ${day}`
      }
    case 'yearly':
      return interval === 1 ? 'Every year' : `Every ${interval} years`
    default:
      return ''
  }
}

export function legacyToRecurrence (repeatPeriod, anchorDate) {
  const start = anchorDate ? moment(anchorDate) : moment()
  switch (repeatPeriod) {
    case 'weekly':
      return {frequency: 'weekly', interval: 1, days_of_week: [start.day()], anchor: anchorDate}
    case 'fortnightly':
      return {frequency: 'weekly', interval: 2, days_of_week: [start.day()], anchor: anchorDate}
    case 'monthly':
      return {frequency: 'monthly', interval: 1, monthly_type: 'day_of_month', day_of_month: start.date(), anchor: anchorDate}
    case '6months':
      return {frequency: 'monthly', interval: 6, monthly_type: 'day_of_month', day_of_month: start.date(), anchor: anchorDate}
    case 'yearly':
      return {frequency: 'yearly', interval: 1, anchor: anchorDate}
    default:
      return defaultRecurrence(anchorDate)
  }
}

export function resolveRecurrenceFromRule (rule) {
  if (rule.recurrence && rule.recurrence.frequency) {
    return rule.recurrence
  }
  return legacyToRecurrence(rule.repeat_period, rule.recurrence && rule.recurrence.anchor || rule.next_run)
}

export function legacyDescribeRecurrence (repeatPeriod) {
  switch (repeatPeriod) {
    case 'weekly': return 'Weekly'
    case 'fortnightly': return 'Every 2 weeks'
    case 'monthly': return 'Monthly'
    case '6months': return 'Every 6 months'
    case 'yearly': return 'Yearly'
    default: return repeatPeriod || 'Unknown'
  }
}

export function applyPreset (presetKey, startDate) {
  const start = startDate ? moment(startDate) : moment()
  const weekday = start.day()
  const dayOfMonth = start.date()

  switch (presetKey) {
    case 'daily':
      return {frequency: 'daily', interval: 1}
    case 'weekdays':
      return {frequency: 'weekly', interval: 1, days_of_week: [1, 2, 3, 4, 5]}
    case 'weekly':
      return {frequency: 'weekly', interval: 1, days_of_week: [weekday]}
    case 'fortnightly':
      return {frequency: 'weekly', interval: 2, days_of_week: [weekday]}
    case 'monthly':
      return {frequency: 'monthly', interval: 1, monthly_type: 'day_of_month', day_of_month: dayOfMonth}
    case 'every_2_months':
      return {frequency: 'monthly', interval: 2, monthly_type: 'day_of_month', day_of_month: dayOfMonth}
    case 'every_3_months':
      return {frequency: 'monthly', interval: 3, monthly_type: 'day_of_month', day_of_month: dayOfMonth}
    case 'every_6_months':
      return {frequency: 'monthly', interval: 6, monthly_type: 'day_of_month', day_of_month: dayOfMonth}
    case 'yearly':
      return {frequency: 'yearly', interval: 1}
    default:
      return defaultRecurrence(startDate)
  }
}

export {WEEKDAY_NAMES, WEEKDAY_SHORT}



// WEBPACK FOOTER //
// ./src/utils/recurrence.js